import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;          // 60s
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;           // 30 min — must match backend IDLE_TIMEOUT_MINUTES

/**
 * Counselor heartbeat + idle auto-logout.
 *
 * - Pings POST /api/auth/heartbeat every 60s while the tab is alive.
 * - Tracks user input (pointerdown / keydown / scroll / touchstart) to compute idle time.
 * - After 30 minutes of zero input, calls onIdleLogout() so the parent dashboard
 *   can hit AuthContext.logout() and bounce to /login.
 *
 * Only meaningful for user_type === "counselor" — pass enabled=false for other roles.
 */
export function useCounselorHeartbeat({ enabled, onIdleLogout }) {
  const lastActivityRef = useRef(Date.now());
  const idleFiredRef = useRef(false);
  const onIdleLogoutRef = useRef(onIdleLogout);

  useEffect(() => {
    onIdleLogoutRef.current = onIdleLogout;
  }, [onIdleLogout]);

  useEffect(() => {
    if (!enabled) return undefined;

    const bump = () => {
      lastActivityRef.current = Date.now();
      idleFiredRef.current = false;
    };

    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      const idleFor = Date.now() - lastActivityRef.current;

      if (idleFor >= IDLE_TIMEOUT_MS && !idleFiredRef.current) {
        idleFiredRef.current = true;
        try {
          if (onIdleLogoutRef.current) onIdleLogoutRef.current();
        } catch (_e) { /* ignore */ }
        return;
      }

      // Send heartbeat only if tab is visible (avoid waking dead tabs)
      if (document.visibilityState === "visible") {
        try {
          await axios.post(`${API}/auth/heartbeat`, {});
        } catch (_e) { /* swallow — heartbeat is best-effort */ }
      }
    };

    // Fire one immediately so the first beat lands fast
    tick();
    const interval = setInterval(tick, HEARTBEAT_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
      events.forEach((ev) => window.removeEventListener(ev, bump));
    };
  }, [enabled]);
}
